import { Component, Inject } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { CompanyDto } from 'src/app/data-transfer/entities/doc-entities/company-dto';
import { CoreDataUpdateService } from 'src/app/data-transfer/services/core-data-update-api-service';
import { PathResolveService } from 'src/app/services/path-resolve.service';

export interface CreateDocDialogData {
  manufacturers: CompanyDto[];
  representatives: CompanyDto[];
}

type CompanySelection = CompanyDto | string | null;

@Component({
  selector: 'app-create-doc-dialog',
  templateUrl: './create-doc-dialog.component.html',
  styleUrl: './create-doc-dialog.component.scss',
})
export class CreateDocDialogComponent {
  readonly manufacturers: CompanyDto[];
  readonly representatives: CompanyDto[];
  readonly organisationSettingsPath = PathResolveService.getOrganisationSettingsPath(true);

  form: FormGroup<{
    manufacturer: FormControl<CompanySelection>;
    representative: FormControl<CompanySelection>;
    hasRepresentative: FormControl<boolean>;
  }>;

  constructor(
    private router: Router,
    private dialogRef: MatDialogRef<CreateDocDialogComponent>,
    private fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) data: CreateDocDialogData,
    private coreDataService: CoreDataUpdateService
  ) {
    this.manufacturers = data.manufacturers;
    this.representatives = data.representatives;
    this.form = this.fb.group({
      manufacturer: new FormControl<CompanySelection>(data.manufacturers[0] ?? null, this.companySelectedValidator()),
      representative: new FormControl<CompanySelection>(null, this.optionalCompanySelectedValidator()),
      hasRepresentative: new FormControl<boolean>(false, { nonNullable: true }),
    });

    this.form.controls.hasRepresentative.valueChanges.subscribe(() => this.form.controls.representative.updateValueAndValidity());
  }

  private companySelectedValidator(): ValidatorFn {
    return (control): ValidationErrors | null => (control.value && typeof control.value === 'object' ? null : { required: true });
  }

  private optionalCompanySelectedValidator(): ValidatorFn {
    return (control): ValidationErrors | null => {
      if (!this.form?.controls.hasRepresentative.value) return null;
      return control.value && typeof control.value === 'object' ? null : { required: true };
    };
  }

  get filteredManufacturers(): CompanyDto[] {
    return this.filterCompanies(this.manufacturers, this.form.controls.manufacturer.value);
  }

  get filteredRepresentatives(): CompanyDto[] {
    return this.filterCompanies(this.representatives, this.form.controls.representative.value);
  }

  private filterCompanies(list: CompanyDto[], value: CompanySelection): CompanyDto[] {
    const term = (typeof value === 'string' ? value : value?.companyName ?? '').trim().toLowerCase();
    if (!term) return list;
    return list.filter(c => c.companyName?.toLowerCase().includes(term));
  }

  displayCompany = (value: CompanySelection): string => {
    if (!value) return '';
    return typeof value === 'string' ? value : value.companyName;
  };

  getCountryName(futureId: string | null): string {
    if (!futureId) return '';
    return this.coreDataService.allCountriesMap.get(futureId)?.name ?? '';
  }

  clickOrganisationSettings(): void {
    this.onCancel();
    const path = PathResolveService.getOrganisationSettingsPath(true);
    this.router.navigate([path]);
  }

  get selectedManufacturer(): CompanyDto | undefined {
    const value = this.form.controls.manufacturer.value;
    return value && typeof value === 'object' ? value : undefined;
  }

  get selectedRepresentative(): CompanyDto | undefined {
    const value = this.form.controls.representative.value;
    return value && typeof value === 'object' ? value : undefined;
  }

  onCancel(): void {
    this.dialogRef.close();
  }

  onContinue(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;

    this.dialogRef.close({
      confirmed: true,
      manufacturerId: this.selectedManufacturer?.companyProfileId,
      authorizedRepresentativeId: this.form.value.hasRepresentative ? (this.selectedRepresentative?.companyProfileId ?? null) : null,
    });
  }
}
